import React, { useMemo, useState } from 'react';
import {
  Button as AntButton,
  ButtonProps,
  Space,
  SpaceProps,
  Dropdown,
  DropdownProps,
  MenuItemProps,
  PopconfirmProps,
  Popconfirm,
  Modal,
  TooltipProps,
  Tooltip,
} from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import { useIntl } from 'react-intl';
import { toString } from 'lodash-es';

export const jumpTo = (href: string, attrs?: Omit<React.AnchorHTMLAttributes<any>, 'href'> & { innerText?: string }) => {
  const a = document.createElement('a');
  a.href = href;
  if (attrs) {
    Object.keys(attrs).forEach((key: string) => {
      a[key] = attrs[key];
    });
  }
  document.body.appendChild(a);

  a.click();

  window.setTimeout(() => {
    document.body.removeChild(a);
  }, 500);
};

export type ButtonItemType = ButtonProps & MenuItemProps & {
  label?: React.ReactNode;
  key?: React.Key;
  confirmText?: PopconfirmProps['title'];
  confirmProps?: Partial<PopconfirmProps>;
  visible?: boolean;
  disabled?: boolean | undefined;
  disabledText?: TooltipProps['title'];
};

export type ButtonGroupProps = {
  items: ButtonItemType[];
  maxSize?: number;
  split?: SpaceProps['split'];
  dropdownProps?: DropdownProps;
  confirmProps?: Partial<PopconfirmProps>;
} & Pick<ButtonProps, 'type' | 'shape' | 'ghost' | 'disabled'>;

export const Button: React.FC<ButtonItemType> = ({
  label,
  children,
  confirmText,
  confirmProps,
  onClick,
  disabled,
  disabledText,
  /* visiblable,  */
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  if (disabled && disabledText) {
    return (
      <Tooltip title={disabledText}>
        <AntButton {...rest} disabled>{label ? label : children}</AntButton>
      </Tooltip>
    );
  }

  if (!disabled && (confirmText || confirmProps?.title)) {
    return (
      <Popconfirm
        {...(confirmProps || { placement: 'left' })}
        title={confirmText || confirmProps?.title}
        onConfirm={async (e: any) => {
          try {
            setLoading(true);
            await onClick?.(e);
          } finally {
            setLoading(false);
          }
        }}
        onCancel={async () => {
          await confirmProps?.onCancel?.();
        }}
      >
        <AntButton loading={loading} {...rest}>
          {label ? label : children}
        </AntButton>
      </Popconfirm>
    );
  }

  return (
    <AntButton
      {...rest}
      disabled={disabled}
      loading={loading}
      onClick={async (e) => {
        try {
          setLoading(true);
          await onClick?.(e);
        } finally {
          setLoading(false);
        }
      }}
    >
      {label ? label : children}
    </AntButton>
  );
};

const ButtonGroup: React.FC<ButtonGroupProps> = ({ items, maxSize, split, dropdownProps, ...props }) => {
  const intl = useIntl();

  const buttons = useMemo(() => {
    const list = items || [];
    if (list.length > maxSize!) {
      return {
        btns: list.slice(0, maxSize! - 1),
        dropdowns: list.slice(maxSize! - 1)
      };
    }

    return {
      btns: list,
      dropdowns: [],
    };
  }, [items, maxSize]);

  return (
    <Space split={split} className='px-xs'>
      {
        buttons.btns.map((rest: ButtonItemType,index:number) => (
          <Button {...props} {...rest} key={index} />
        ))
      }
      {
        buttons.dropdowns.length > 0 && (
          <Dropdown
            placement="bottomRight"
            {...(dropdownProps || {})}
            menu={{
              items: buttons.dropdowns.map(({ confirmText, confirmProps, onClick, disabled,disabledText,...item}, index) => ({
                key: index,
                style: {
                  minWidth: 120,
                  whiteSpace: 'nowrap'
                },
                ...item,
                disabled: disabled,
                title: disabled && disabledText ? disabledText as string : '',
                onClick: async (e) => {
                  if (confirmText || confirmProps?.title) {
                    Modal.confirm({
                      ...(confirmProps || {}),
                      title: intl.formatMessage({ defaultMessage: '提示信息' }),
                      content: (confirmText || confirmProps?.title) as React.ReactNode,
                      async onOk() {
                        try {
                          if (onClick) {
                            await onClick?.(e);
                          } else if (item.href) {
                            jumpTo(item.href, { target: item.target, innerText: toString(item.label) });
                          }
                        } catch (e) {
                          console.log(e);
                        }
                      },
                      async onCancel() {
                        await confirmProps?.onCancel?.();
                      }
                    });
                  } else {
                    if (onClick) {
                      await onClick?.(e);
                    } else if (item.href) {
                      jumpTo(item.href, { target: item.target, innerText: toString(item.label) });
                    }
                  }
                }
              }))
            }}
          >
            <AntButton {...props}>
              <EllipsisOutlined />
            </AntButton>
          </Dropdown>
        )
      }
    </Space>
  );
};

ButtonGroup.defaultProps = {
  type: 'link',
  maxSize: 3,
};



export default ButtonGroup;
